import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, ShieldCheck, UserPlus, KeyRound, Trash2, Copy, Check, Search, Building2, Mail, MailCheck, History, Info } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { admin, auth } from '@/lib/api';

const ROLES = ['super_admin', 'Principal RMA', 'Case Manager', 'Paralegal', 'Client Portal'];

export default function AdminConsolePage() {
  const me = auth.currentUser();
  const isSuper = me?.role === 'super_admin';

  const [orgs, setOrgs] = React.useState([]);
  const [users, setUsers] = React.useState([]);
  const [q, setQ] = React.useState('');
  const [orgFilter, setOrgFilter] = React.useState('all');
  const [loading, setLoading] = React.useState(true);
  const [issued, setIssued] = React.useState(null); // { email, temp_password }

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const [uRes, oRes] = await Promise.all([
        admin.listUsers({ q: q || undefined, org_id: orgFilter !== 'all' ? orgFilter : undefined }),
        admin.listOrgs(),
      ]);
      setUsers(uRes.items || []);
      setOrgs(oRes.items || []);
    } catch (e) { /* noop */ }
    finally { setLoading(false); }
  }, [q, orgFilter]);

  React.useEffect(() => { if (isSuper) load(); }, [isSuper, load]);

  if (!isSuper) {
    return (
      <div className="p-8">
        <Card className="border-slate-200 max-w-lg mx-auto" data-testid="admin-forbidden">
          <CardContent className="p-8 text-center">
            <ShieldCheck className="mx-auto h-8 w-8 text-slate-400" />
            <div className="mt-3 text-sm font-semibold text-[#1A3A5C]">Super admin only</div>
            <div className="mt-1 text-xs text-slate-500">This console is only accessible to platform super admins.</div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="admin-console">
      <div className="flex items-center gap-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-md bg-[#2ECC9A]/15">
          <ShieldCheck className="h-4 w-4 text-[#2ECC9A]" />
        </div>
        <div className="flex-1">
          <h1 className="font-serif text-3xl font-bold tracking-tight text-[#1A3A5C]">Admin console</h1>
          <div className="text-sm text-slate-500">Invite test users and admins across every firm on the platform.</div>
        </div>
        <a
          href="/app/settings#sender-domain"
          data-testid="admin-sender-domain-link"
          className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-3 py-1.5 text-xs text-[#1A3A5C] hover:border-[#2ECC9A] hover:text-[#2ECC9A]"
          title="Verify a sender domain so invites can go to any email address"
        >
          <Mail className="h-3.5 w-3.5" /> Verify sender domain →
        </a>
      </div>

      {issued && <InvitedCredential issued={issued} onClose={() => setIssued(null)} />}

      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <InviteForm orgs={orgs} onIssued={(x) => { setIssued(x); load(); }} />

        <Card className="border-slate-200 lg:col-span-2" data-testid="admin-users-card">
          <CardContent className="p-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-[#1A3A5C]">Users</div>
                <div className="text-xs text-slate-500">{users.length} account{users.length === 1 ? '' : 's'} across {orgs.length} org{orgs.length === 1 ? '' : 's'}.</div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                  <Input
                    data-testid="admin-user-search"
                    placeholder="Search email or name"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    className="h-9 w-56 pl-8 text-xs"
                  />
                </div>
                <Select value={orgFilter} onValueChange={setOrgFilter}>
                  <SelectTrigger data-testid="admin-org-filter" className="h-9 w-56 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All orgs</SelectItem>
                    {orgs.map((o) => (<SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {loading ? (
              <div className="mt-6 flex justify-center py-6"><Loader2 className="h-4 w-4 animate-spin text-slate-400" /></div>
            ) : users.length === 0 ? (
              <div className="mt-6 rounded-md border border-dashed border-slate-200 p-6 text-center text-xs text-slate-500">
                No users match — try clearing filters or invite someone.
              </div>
            ) : (
              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">
                      <th className="py-2 pr-3 font-medium">Name / Email</th>
                      <th className="py-2 pr-3 font-medium">Firm</th>
                      <th className="py-2 pr-3 font-medium">Role</th>
                      <th className="py-2 pr-3 font-medium">Created</th>
                      <th className="py-2 pr-3 text-right font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody data-testid="admin-users-tbody">
                    {users.map((u) => (
                      <UserRow key={u.id} u={u} me={me} onChanged={load} onIssued={setIssued} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="mt-6">
        <Card className="border-slate-200" data-testid="admin-orgs-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 text-sm font-semibold text-[#1A3A5C]">
              <Building2 className="h-4 w-4 text-[#2ECC9A]" /> Firms on the platform
            </div>
            <div className="mt-4 grid gap-3 md:grid-cols-3">
              {orgs.map((o) => (
                <div key={o.id} data-testid={`admin-org-${o.id}`} className="rounded-md border border-slate-200 p-3">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium text-[#1A3A5C]">{o.name}</div>
                    <Badge variant="outline" className="border-slate-200 text-[10px] text-slate-500">{o.kind}</Badge>
                  </div>
                  <div className="mt-1 text-[11px] text-slate-500">{o.user_count} user{o.user_count === 1 ? '' : 's'} · {(o.id || '').slice(0, 8)}…</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="mt-6">
        <AuditLogCard refreshKey={users.length + orgs.length} />
      </div>
    </div>
  );
}


function InviteForm({ orgs, onIssued }) {
  const [form, setForm] = React.useState({
    email: '', name: '', role: 'Principal RMA', marn: '',
    org_choice: 'new', org_id: '', firm_name: '',
    send_email: false,
  });
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState('');

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr('');
    const payload = {
      email: form.email.trim(),
      name: form.name.trim(),
      role: form.role,
      marn: form.marn.trim() || undefined,
      send_email: form.send_email,
    };
    if (form.org_choice === 'existing') payload.org_id = form.org_id;
    else payload.firm_name = form.firm_name.trim() || `${form.name}'s firm`;
    try {
      const r = await admin.invite(payload);
      onIssued({
        email: r.user.email,
        temp_password: r.temp_password,
        role: r.user.role,
        firm: r.firm_name,
        email_status: r.email_status,
      });
      setForm((f) => ({ ...f, email: '', name: '', marn: '', firm_name: '' }));
    } catch (ex) {
      setErr(ex?.response?.data?.detail || 'Invite failed');
    } finally { setBusy(false); }
  };

  return (
    <Card className="border-slate-200" data-testid="admin-invite-card">
      <CardContent className="p-6">
        <div className="flex items-center gap-2 text-sm font-semibold text-[#1A3A5C]">
          <UserPlus className="h-4 w-4 text-[#2ECC9A]" /> Invite user
        </div>
        <div className="mt-1 text-xs text-slate-500">Creates the account instantly and returns a temp password you can share.</div>

        <form onSubmit={submit} className="mt-4 space-y-3" data-testid="admin-invite-form">
          <div>
            <Label className="text-xs">Email</Label>
            <Input data-testid="admin-invite-email" type="email" required value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="mt-1 h-9" placeholder="test.user@example.com" />
          </div>
          <div>
            <Label className="text-xs">Full name</Label>
            <Input data-testid="admin-invite-name" required value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="mt-1 h-9" placeholder="Jane Doe" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="admin-invite-role" className="mt-1 h-9 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">MARN (optional)</Label>
              <Input data-testid="admin-invite-marn" value={form.marn}
                onChange={(e) => setForm({ ...form, marn: e.target.value })}
                className="mt-1 h-9" placeholder="1234567" />
            </div>
          </div>

          <div>
            <Label className="text-xs">Firm</Label>
            <div className="mt-1 flex items-center gap-2 text-xs text-slate-500">
              <label className="inline-flex items-center gap-1">
                <input type="radio" checked={form.org_choice === 'new'} onChange={() => setForm({ ...form, org_choice: 'new' })} data-testid="admin-invite-new-firm-radio" /> New firm
              </label>
              <label className="inline-flex items-center gap-1">
                <input type="radio" checked={form.org_choice === 'existing'} onChange={() => setForm({ ...form, org_choice: 'existing', org_id: orgs[0]?.id || '' })} data-testid="admin-invite-existing-firm-radio" /> Existing firm
              </label>
            </div>
            {form.org_choice === 'new' ? (
              <Input data-testid="admin-invite-firm-name" value={form.firm_name}
                onChange={(e) => setForm({ ...form, firm_name: e.target.value })}
                className="mt-2 h-9" placeholder="Acme Migration" />
            ) : (
              <Select value={form.org_id} onValueChange={(v) => setForm({ ...form, org_id: v })}>
                <SelectTrigger data-testid="admin-invite-existing-firm" className="mt-2 h-9 text-xs"><SelectValue placeholder="Pick a firm" /></SelectTrigger>
                <SelectContent>{orgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
              </Select>
            )}
          </div>

          {err && <div className="rounded-md bg-red-50 p-2 text-[11px] text-red-700">{err}</div>}

          <div className="flex items-center justify-between rounded-md border border-slate-200 bg-slate-50/50 px-3 py-2">
            <div className="flex items-start gap-2">
              <Mail className="mt-0.5 h-3.5 w-3.5 text-[#2ECC9A]" />
              <div>
                <div className="text-xs font-medium text-[#1A3A5C]">Send credentials by email</div>
                <div className="text-[10px] text-slate-500">Deliver the temp password via Resend to the invitee's inbox.</div>
              </div>
            </div>
            <Switch
              data-testid="admin-invite-send-email"
              checked={form.send_email}
              onCheckedChange={(v) => setForm({ ...form, send_email: v })}
            />
          </div>

          <Button
            data-testid="admin-invite-submit"
            type="submit"
            disabled={busy || !form.email || !form.name}
            className="w-full gap-1 bg-[#1A3A5C] text-white hover:bg-[#1A3A5C]/90"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
            Invite & generate password
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}


function InvitedCredential({ issued, onClose }) {
  const [copied, setCopied] = React.useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(`${issued.email} / ${issued.temp_password}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch { /* noop */ }
  };
  const es = issued.email_status || {};
  return (
    <div className="mt-4 rounded-md border border-[#2ECC9A]/40 bg-[#2ECC9A]/5 p-4" data-testid="admin-invite-result">
      <div className="flex items-center gap-2 text-sm font-semibold text-[#1A3A5C]">
        <Check className="h-4 w-4 text-[#2ECC9A]" /> Credential generated
      </div>
      <div className="mt-2 grid gap-2 md:grid-cols-3">
        <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Email</div><div className="font-mono text-xs text-[#1A3A5C]" data-testid="admin-invite-result-email">{issued.email}</div></div>
        <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Temp password</div><div className="font-mono text-xs text-[#1A3A5C]" data-testid="admin-invite-result-password">{issued.temp_password}</div></div>
        <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Role · Firm</div><div className="text-xs text-[#1A3A5C]">{issued.role} · {issued.firm}</div></div>
      </div>
      {es.requested && (
        <div className="mt-3 flex items-center gap-2 rounded-md bg-white/70 px-3 py-1.5 text-[11px]" data-testid="admin-invite-result-email-status">
          {es.sent ? (
            <><MailCheck className="h-3.5 w-3.5 text-[#2ECC9A]" /> <span className="text-[#1A3A5C]">Emailed to {issued.email} via {es.provider || 'Resend'}</span></>
          ) : (
            <><Info className="h-3.5 w-3.5 text-amber-600" /> <span className="text-amber-700">Email not delivered ({es.provider || 'dev'}): {es.error || es.note || 'no provider configured'}</span></>
          )}
        </div>
      )}
      <div className="mt-3 flex items-center gap-2">
        <Button data-testid="admin-invite-copy" size="sm" variant="outline" onClick={copy} className="h-8 gap-1 border-slate-200 text-xs">
          {copied ? <Check className="h-3.5 w-3.5 text-[#2ECC9A]" /> : <Copy className="h-3.5 w-3.5" />} {copied ? 'Copied' : 'Copy'}
        </Button>
        <Button size="sm" variant="ghost" onClick={onClose} className="h-8 text-xs text-slate-500">Dismiss</Button>
        <span className="ml-auto text-[10px] text-slate-500">Share this — the raw password will not be shown again.</span>
      </div>
    </div>
  );
}


function UserRow({ u, me, onChanged, onIssued }) {
  const [busy, setBusy] = React.useState(null);

  const changeRole = async (role) => {
    if (role === u.role) return;
    setBusy('role');
    try { await admin.setRole(u.id, role); onChanged(); }
    catch { /* noop */ }
    finally { setBusy(null); }
  };

  const resetPw = async () => {
    const send = window.confirm(
      `Reset password for ${u.email}?\n\nOK = send them the new password by email.\nCancel = just show me the credentials.`
    );
    setBusy('reset');
    try {
      const r = await admin.resetPassword(u.id, undefined, send);
      onIssued({
        email: u.email,
        temp_password: r.temp_password,
        role: u.role,
        firm: u.firm_name || u.firm,
        email_status: r.email_status,
      });
      onChanged();
    } catch { /* noop */ }
    finally { setBusy(null); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete ${u.email}? This cannot be undone.`)) return;
    setBusy('remove');
    try { await admin.remove(u.id); onChanged(); }
    catch { /* noop */ }
    finally { setBusy(null); }
  };

  const isSelf = u.id === me?.id;

  return (
    <tr className="border-t border-slate-100 text-xs" data-testid={`admin-user-row-${u.id}`}>
      <td className="py-2 pr-3">
        <div className="font-medium text-[#1A3A5C]">{u.name || '—'}</div>
        <div className="text-[10px] text-slate-500">{u.email}{isSelf && <span className="ml-1 text-[#2ECC9A]">(you)</span>}</div>
      </td>
      <td className="py-2 pr-3 text-slate-600">{u.firm_name || u.firm || '—'}</td>
      <td className="py-2 pr-3">
        <Select value={u.role} onValueChange={changeRole} disabled={busy === 'role' || isSelf}>
          <SelectTrigger className="h-8 w-40 text-xs" data-testid={`admin-user-role-${u.id}`}><SelectValue /></SelectTrigger>
          <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
        </Select>
      </td>
      <td className="py-2 pr-3 text-[10px] text-slate-500">{u.created_at ? new Date(u.created_at).toLocaleDateString('en-AU') : '—'}</td>
      <td className="py-2 pr-3 text-right">
        <div className="inline-flex items-center gap-1.5">
          <Button
            data-testid={`admin-user-reset-${u.id}`}
            variant="outline" size="sm"
            onClick={resetPw}
            disabled={!!busy}
            className="h-7 gap-1 border-slate-200 text-[10px]"
            title="Reset password"
          >
            {busy === 'reset' ? <Loader2 className="h-3 w-3 animate-spin" /> : <KeyRound className="h-3 w-3" />} Reset
          </Button>
          <Button
            data-testid={`admin-user-delete-${u.id}`}
            variant="outline" size="sm"
            onClick={remove}
            disabled={!!busy || isSelf}
            className="h-7 gap-1 border-slate-200 text-[10px] text-red-600 hover:bg-red-50 hover:text-red-700 hover:border-red-200"
            title={isSelf ? "Can't delete yourself" : 'Delete user'}
          >
            {busy === 'remove' ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
          </Button>
        </div>
      </td>
    </tr>
  );
}



// -------------------------------- Audit log --------------------------------
const ACTION_LABEL = {
  'user.invite': { label: 'Invited', tone: 'bg-[#2ECC9A]/10 text-[#1A3A5C] border-[#2ECC9A]/30' },
  'user.role_change': { label: 'Role change', tone: 'bg-sky-50 text-sky-700 border-sky-200' },
  'user.password_reset': { label: 'Password reset', tone: 'bg-amber-50 text-amber-700 border-amber-200' },
  'user.delete': { label: 'Deleted', tone: 'bg-red-50 text-red-700 border-red-200' },
};

function AuditLogCard({ refreshKey }) {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [actionFilter, setActionFilter] = React.useState('all');

  const load = React.useCallback(() => {
    setLoading(true);
    admin.audit({ limit: 50, ...(actionFilter !== 'all' ? { action: actionFilter } : {}) })
      .then((r) => setItems(r.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [actionFilter]);

  React.useEffect(() => { load(); }, [load, refreshKey]);

  return (
    <Card className="border-slate-200" data-testid="admin-audit-card">
      <CardContent className="p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-sm font-semibold text-[#1A3A5C]">
              <History className="h-4 w-4 text-[#2ECC9A]" /> Admin audit log
            </div>
            <div className="text-xs text-slate-500">Every invite, reset, role change, and delete is recorded. Read-only.</div>
          </div>
          <Select value={actionFilter} onValueChange={setActionFilter}>
            <SelectTrigger data-testid="admin-audit-filter" className="h-9 w-52 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All actions</SelectItem>
              <SelectItem value="user.invite">Invite</SelectItem>
              <SelectItem value="user.role_change">Role change</SelectItem>
              <SelectItem value="user.password_reset">Password reset</SelectItem>
              <SelectItem value="user.delete">Delete</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="mt-6 flex justify-center py-6"><Loader2 className="h-4 w-4 animate-spin text-slate-400" /></div>
        ) : items.length === 0 ? (
          <div className="mt-6 rounded-md border border-dashed border-slate-200 p-6 text-center text-xs text-slate-500">
            No audit events yet. Invite a user or reset a password to see the trail.
          </div>
        ) : (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">
                  <th className="py-2 pr-3 font-medium">When</th>
                  <th className="py-2 pr-3 font-medium">Action</th>
                  <th className="py-2 pr-3 font-medium">Actor</th>
                  <th className="py-2 pr-3 font-medium">Target</th>
                  <th className="py-2 pr-3 font-medium">Details</th>
                  <th className="py-2 pr-3 font-medium">IP</th>
                </tr>
              </thead>
              <tbody data-testid="admin-audit-tbody">
                {items.map((ev) => {
                  const cfg = ACTION_LABEL[ev.action] || { label: ev.action, tone: 'bg-slate-50 text-slate-700 border-slate-200' };
                  const meta = ev.meta || {};
                  let detail = '';
                  if (ev.action === 'user.invite') detail = `${meta.role || ''} · ${meta.firm || ''}${meta.emailed ? ' · emailed' : ''}`;
                  else if (ev.action === 'user.role_change') detail = `${meta.from || '—'} → ${meta.to || '—'}`;
                  else if (ev.action === 'user.password_reset') detail = meta.emailed ? 'emailed to user' : 'shown in UI';
                  else if (ev.action === 'user.delete') detail = `${meta.role || ''}${meta.firm ? ' · ' + meta.firm : ''}`;
                  return (
                    <tr key={ev.id} data-testid={`admin-audit-row-${ev.id}`} className="border-t border-slate-100">
                      <td className="whitespace-nowrap py-2 pr-3 text-[10px] text-slate-500">{new Date(ev.created_at).toLocaleString('en-AU')}</td>
                      <td className="py-2 pr-3"><Badge variant="outline" className={`border ${cfg.tone} text-[10px]`}>{cfg.label}</Badge></td>
                      <td className="py-2 pr-3 text-[#1A3A5C]">{ev.actor_email || '—'}</td>
                      <td className="py-2 pr-3 text-slate-600">{ev.target_email || '—'}</td>
                      <td className="py-2 pr-3 text-[10px] text-slate-500">{detail}</td>
                      <td className="whitespace-nowrap py-2 pr-3 font-mono text-[10px] text-slate-500">{ev.ip || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
